import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  Video,
  Type,
  Music,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Loader2,
  Play,
  X,
  Users,
  MessageSquare,
  Send,
  CameraOff,
  Camera,
} from 'lucide-react';

interface SuggestedClip {
  assetName: string;
  trim?: {
    from: number;
    to: number;
  };
  speaker?: string;
  reason: string;
}

interface Scene {
  id: string;
  description: string;
  scriptLine: string;
  scriptCharacter?: string;
  visualDirection: string;
  estimatedDuration: number;
  suggestedClips: SuggestedClip[] | string[];
  confidence: number;
}

interface SpeakerMapping {
  character: string;
  confidence: number;
  reasoning: string;
}

interface SpeakerAnalysis {
  scriptCharacters: string[];
  detectedSpeakers: string[];
  suggestedMapping: Record<string, SpeakerMapping>;
  mismatchWarning?: string;
}

interface Question {
  id: string;
  type: string;
  sceneId?: string;
  question: string;
  options?: string[];
  context?: string;
  // For speaker_mapping questions
  detectedSpeakers?: string[];
  scriptCharacters?: string[];
  suggestedMapping?: Record<string, string>;
  // For speaker_role questions
  character?: string;
}

interface TextOverlay {
  text: string;
  sceneId: string;
  position: string;
  style: string;
}

interface ChatMessage {
  role: 'ai' | 'user';
  content: string;
  timestamp: number;
}

interface ScriptAnalysis {
  speakerAnalysis?: SpeakerAnalysis;
  scenes: Scene[];
  questions: Question[];
  textOverlays: TextOverlay[];
  transitions: Array<{
    from: string;
    to: string;
    type: string;
    reason: string;
  }>;
  musicMood: string;
  totalDuration: number;
  missingFootage: string[];
  editNotes: string;
}

interface ScriptAnalysisChatProps {
  analysis: ScriptAnalysis;
  answers: Record<string, string>;
  onAnswerChange: (questionId: string, answer: string) => void;
  onGeneratePlan: (selectedClips?: Record<string, number>) => void;
  isGenerating: boolean;
  onClose: () => void;
  // New props for chat functionality
  conversation?: ChatMessage[];
  onSendMessage?: (message: string) => void;
  isRefining?: boolean;
}


export const ScriptAnalysisChat: React.FC<ScriptAnalysisChatProps> = ({
  analysis,
  answers,
  onAnswerChange,
  onGeneratePlan,
  isGenerating,
  onClose,
  conversation = [],
  onSendMessage,
  isRefining = false,
}) => {
  const [expandedScenes, setExpandedScenes] = useState<Set<string>>(new Set());
  const [refinementInput, setRefinementInput] = useState('');
  const [speakerMappings, setSpeakerMappings] = useState<Record<string, { character: string; offCamera: boolean }>>({});
  // Track which clip is selected for each scene (index into suggestedClips array)
  const [selectedClipPerScene, setSelectedClipPerScene] = useState<Record<string, number>>({});

  // Auto-populate speaker mappings from suggestions when analysis loads
  useEffect(() => {
    const speakerQuestion = analysis?.questions?.find(q => q.type === 'speaker_mapping');
    if (speakerQuestion?.suggestedMapping && speakerQuestion.detectedSpeakers) {
      const initialMappings: Record<string, { character: string; offCamera: boolean }> = {};
      let hasMapping = false;

      for (const speaker of speakerQuestion.detectedSpeakers) {
        const suggestedChar = speakerQuestion.suggestedMapping[speaker];
        if (suggestedChar) {
          initialMappings[speaker] = { character: suggestedChar, offCamera: false };
          hasMapping = true;
        }
      }

      if (hasMapping) {
        setSpeakerMappings(initialMappings);
        // Also set the answer so it counts as answered
        const answerStr = Object.entries(initialMappings)
          .filter(([, val]) => val.character)
          .map(([spk, val]) => `${spk}=${val.character}`)
          .join(', ');
        onAnswerChange(speakerQuestion.id, answerStr);
      }
    }
  }, [analysis?.questions]);

  const toggleScene = (sceneId: string) => {
    setExpandedScenes(prev => {
      const next = new Set(prev);
      if (next.has(sceneId)) {
        next.delete(sceneId);
      } else {
        next.add(sceneId);
      }
      return next;
    });
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'bg-green-500/20 text-green-600';
    if (confidence >= 0.5) return 'bg-yellow-500/20 text-yellow-600';
    return 'bg-red-500/20 text-red-600';
  };

  const handleSendRefinement = () => {
    if (refinementInput.trim() && onSendMessage) {
      onSendMessage(refinementInput.trim());
      setRefinementInput('');
    }
  };


  const handleSpeakerMappingChange = (questionId: string, speaker: string, character: string) => {
    setSpeakerMappings(prev => ({
      ...prev,
      [speaker]: { ...prev[speaker], character }
    }));
    // Build the answer string from all mappings
    const updatedMappings = { ...speakerMappings, [speaker]: { ...speakerMappings[speaker], character } };
    const answerStr = Object.entries(updatedMappings)
      .filter(([, val]) => val.character)
      .map(([spk, val]) => `${spk}=${val.character}${val.offCamera ? ' (off-camera)' : ''}`)
      .join(', ');
    onAnswerChange(questionId, answerStr);
  };

  const handleOffCameraChange = (questionId: string, speaker: string, offCamera: boolean) => {
    setSpeakerMappings(prev => ({
      ...prev,
      [speaker]: { ...prev[speaker], offCamera }
    }));
    // Rebuild the answer string
    const updatedMappings = { ...speakerMappings, [speaker]: { ...speakerMappings[speaker], offCamera } };
    const answerStr = Object.entries(updatedMappings)
      .filter(([, val]) => val.character)
      .map(([spk, val]) => `${spk}=${val.character}${val.offCamera ? ' (off-camera)' : ''}`)
      .join(', ');
    onAnswerChange(questionId, answerStr);
  };

  const unansweredQuestions = analysis.questions?.filter(q => !answers[q.id]) || [];
  const canGenerate = unansweredQuestions.length === 0;

  // Separate question types for better rendering
  const speakerQuestions = analysis.questions?.filter(q => q.type === 'speaker_mapping' || q.type === 'speaker_role') || [];
  const clipQuestions = analysis.questions?.filter(q => q.type === 'clip_selection') || [];
  const otherQuestions = analysis.questions?.filter(q => !['speaker_mapping', 'speaker_role', 'clip_selection'].includes(q.type)) || [];

  // Guard against malformed analysis
  if (!analysis || !analysis.scenes) {
    return (
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-500" />
            Script Analysis
          </h3>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">No analysis data available. Add clips to your project and try again.</p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-500" />
          Script Analysis
        </h3>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="h-[400px] pr-2">
        {/* Conversation History */}
        {conversation.length > 0 && (
          <div className="space-y-2 mb-4">
            <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              Conversation
            </h4>
            <div className="space-y-2">
              {conversation.map((msg, i) => (
                <div
                  key={i}
                  className={`text-xs p-2 rounded-lg ${
                    msg.role === 'ai'
                      ? 'bg-purple-500/10 border border-purple-500/20'
                      : 'bg-blue-500/10 border border-blue-500/20 ml-4'
                  }`}
                >
                  <div className="flex items-center gap-1 mb-1">
                    {msg.role === 'ai' ? (
                      <Sparkles className="h-3 w-3 text-purple-500" />
                    ) : (
                      <MessageSquare className="h-3 w-3 text-blue-500" />
                    )}
                    <span className="font-medium">
                      {msg.role === 'ai' ? 'AI Editor' : 'You'}
                    </span>
                    <span className="text-[9px] text-muted-foreground ml-auto">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-muted-foreground">{msg.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Speaker Analysis Summary */}
        {analysis.speakerAnalysis && (
          <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <Users className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-purple-600 mb-2">Speaker Analysis</p>
                <div className="text-xs space-y-1">
                  <p className="text-muted-foreground">
                    <span className="font-medium">Script characters:</span> {analysis.speakerAnalysis.scriptCharacters?.join(', ') || 'None detected'}
                  </p>
                  <p className="text-muted-foreground">
                    <span className="font-medium">Detected speakers:</span> {analysis.speakerAnalysis.detectedSpeakers?.join(', ') || 'None detected'}
                  </p>
                  {analysis.speakerAnalysis.mismatchWarning && (
                    <p className="text-yellow-600 mt-2 flex items-start gap-1">
                      <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                      {analysis.speakerAnalysis.mismatchWarning}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="bg-muted/50 rounded-lg p-3 mb-4 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Total Duration:</span>
            <span className="font-medium">{analysis.totalDuration}s</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Scenes:</span>
            <span className="font-medium">{analysis.scenes?.length || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Text Overlays:</span>
            <span className="font-medium">{analysis.textOverlays?.length || 0}</span>
          </div>
          {analysis.musicMood && (
            <div className="flex items-center gap-2">
              <Music className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">{analysis.musicMood}</span>
            </div>
          )}
        </div>

        {/* Missing Footage Warning */}
        {analysis.missingFootage && analysis.missingFootage.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-yellow-600">Missing Footage</p>
                <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  {analysis.missingFootage.map((item, i) => (
                    <li key={i}>- {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Speaker Questions (Priority) */}
        {speakerQuestions.length > 0 && (
          <div className="space-y-3 mb-4">
            <h4 className="text-xs font-medium text-purple-600 flex items-center gap-1">
              <Users className="h-3 w-3" />
              Speaker Setup (Answer First)
            </h4>
            {speakerQuestions.map((question) => (
              <div
                key={question.id}
                className={`border-2 border-purple-500/30 rounded-lg p-3 ${
                  answers[question.id] ? 'bg-green-500/5 border-green-500/20' : 'bg-purple-500/5'
                }`}
              >
                <p className="text-xs font-medium mb-2">{question.question}</p>
                {question.context && (
                  <p className="text-[10px] text-muted-foreground mb-3 italic">
                    {question.context}
                  </p>
                )}

                {question.type === 'speaker_mapping' && question.detectedSpeakers && question.scriptCharacters && (
                  <div className="space-y-2">
                    {question.detectedSpeakers.map((speaker) => {
                      const suggestedChar = question.suggestedMapping?.[speaker];
                      return (
                        <div key={speaker} className="flex items-center gap-2 p-2 bg-muted/30 rounded">
                          <span className="text-xs font-medium w-20">{speaker}:</span>
                          <Select
                            value={speakerMappings[speaker]?.character || ''}
                            onValueChange={(value) => handleSpeakerMappingChange(question.id, speaker, value)}
                          >
                            <SelectTrigger className="h-7 text-xs flex-1">
                              <SelectValue placeholder={suggestedChar ? `Suggested: ${suggestedChar}` : "Select character..."} />
                            </SelectTrigger>
                            <SelectContent>
                              {question.scriptCharacters.map((char) => (
                                <SelectItem key={char} value={char} className="text-xs">
                                  {char} {suggestedChar === char && '(suggested)'}
                                </SelectItem>
                              ))}
                              <SelectItem value="__ignore__" className="text-xs text-muted-foreground">
                                (Ignore this speaker)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="flex items-center gap-1">
                            <Checkbox
                              id={`offcamera-${speaker}`}
                              checked={speakerMappings[speaker]?.offCamera || false}
                              onCheckedChange={(checked) =>
                                handleOffCameraChange(question.id, speaker, checked as boolean)
                              }
                            />
                            <Label htmlFor={`offcamera-${speaker}`} className="text-[10px] flex items-center gap-1 cursor-pointer">
                              <CameraOff className="h-3 w-3" />
                              Off-camera
                            </Label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {question.type === 'speaker_role' && question.options && (
                  <RadioGroup
                    value={answers[question.id] || ''}
                    onValueChange={(value) => onAnswerChange(question.id, value)}
                    className="space-y-1"
                  >
                    {question.options.map((option, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`${question.id}-${i}`} />
                        <Label
                          htmlFor={`${question.id}-${i}`}
                          className="text-xs cursor-pointer flex items-center gap-1"
                        >
                          {option.includes('Off-camera') ? (
                            <CameraOff className="h-3 w-3 text-orange-500" />
                          ) : (
                            <Camera className="h-3 w-3 text-green-500" />
                          )}
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}

                {answers[question.id] && (
                  <div className="flex items-center gap-1 mt-2 text-green-600">
                    <CheckCircle2 className="h-3 w-3" />
                    <span className="text-[10px]">Configured</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Scenes */}
        <div className="space-y-2 mb-4">
          <h4 className="text-xs font-medium text-muted-foreground">Scenes</h4>
          {analysis.scenes?.map((scene) => (
            <div
              key={scene.id}
              className="border rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleScene(scene.id)}
                className="w-full flex items-center gap-2 p-2 text-left hover:bg-muted/50 transition-colors"
              >
                {expandedScenes.has(scene.id) ? (
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                )}
                <Video className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-medium flex-1 truncate">
                  {scene.description}
                </span>
                {scene.scriptCharacter && (
                  <Badge variant="outline" className="text-[9px]">
                    {scene.scriptCharacter}
                  </Badge>
                )}
                <Badge
                  variant="secondary"
                  className={`text-[10px] ${getConfidenceColor(scene.confidence)}`}
                >
                  {Math.round(scene.confidence * 100)}%
                </Badge>
                <span className="text-[10px] text-muted-foreground">
                  {scene.estimatedDuration}s
                </span>
              </button>

              {expandedScenes.has(scene.id) && (
                <div className="p-2 pt-0 space-y-2 text-xs">
                  {scene.scriptLine && (
                    <div>
                      <span className="text-muted-foreground">Script: </span>
                      <span className="italic">"{scene.scriptLine}"</span>
                    </div>
                  )}
                  {scene.visualDirection && (
                    <div>
                      <span className="text-muted-foreground">Visual: </span>
                      <span>{scene.visualDirection}</span>
                    </div>
                  )}
                  {scene.suggestedClips && scene.suggestedClips.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-muted-foreground">Selected Clip: </span>
                      {(() => {
                        const selectedIdx = selectedClipPerScene[scene.id] || 0;
                        const clip = scene.suggestedClips[selectedIdx];
                        const isOldFormat = typeof clip === 'string';
                        const clipName = isOldFormat ? clip : (clip as SuggestedClip).assetName;
                        const trim = isOldFormat ? null : (clip as SuggestedClip).trim;
                        const clipReason = isOldFormat ? null : (clip as SuggestedClip).reason;
                        const clipSpeaker = isOldFormat ? null : (clip as SuggestedClip).speaker;

                        return (
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <Badge variant="outline" className="text-[10px] bg-purple-50 dark:bg-purple-900/30 border-purple-300">
                                {clipName}
                                {trim && (
                                  <span className="text-green-500 ml-1">
                                    [{trim.from.toFixed(1)}s-{trim.to.toFixed(1)}s]
                                  </span>
                                )}
                                {clipSpeaker && (
                                  <span className="text-purple-500 ml-1">
                                    ({clipSpeaker})
                                  </span>
                                )}
                              </Badge>
                              {clipReason && (
                                <span className="text-[9px] text-muted-foreground block mt-0.5">
                                  {clipReason}
                                </span>
                              )}
                            </div>
                            {/* Show dropdown if multiple clips available */}
                            {scene.suggestedClips.length > 1 && (
                              <Select
                                value={String(selectedIdx)}
                                onValueChange={(val) => {
                                  setSelectedClipPerScene(prev => ({
                                    ...prev,
                                    [scene.id]: parseInt(val)
                                  }));
                                }}
                              >
                                <SelectTrigger className="w-[140px] h-6 text-[10px]">
                                  <SelectValue placeholder="Switch take..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {scene.suggestedClips.map((altClip, altIdx) => {
                                    const altIsOld = typeof altClip === 'string';
                                    const altName = altIsOld ? altClip : (altClip as SuggestedClip).assetName;
                                    // Truncate long names
                                    const displayName = altName.length > 25 ? altName.slice(0, 22) + '...' : altName;
                                    return (
                                      <SelectItem key={altIdx} value={String(altIdx)} className="text-[10px]">
                                        {altIdx === 0 ? '⭐ ' : ''}{displayName}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        );
                      })()}
                      {/* Show other available takes as smaller badges */}
                      {scene.suggestedClips.length > 1 && (
                        <div className="text-[9px] text-muted-foreground mt-1">
                          {scene.suggestedClips.length - 1} other take{scene.suggestedClips.length > 2 ? 's' : ''} available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Text Overlays */}
        {analysis.textOverlays && analysis.textOverlays.length > 0 && (
          <div className="space-y-2 mb-4">
            <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Type className="h-3 w-3" />
              Text Overlays
            </h4>
            {analysis.textOverlays.map((overlay, i) => (
              <div key={i} className="border rounded-lg p-2 text-xs">
                <p className="font-medium">"{overlay.text}"</p>
                <p className="text-muted-foreground text-[10px]">
                  {overlay.style} at {overlay.position}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Clip Selection Questions */}
        {clipQuestions.length > 0 && (
          <div className="space-y-3 mb-4">
            <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <HelpCircle className="h-3 w-3" />
              Clip Choices ({clipQuestions.filter(q => !answers[q.id]).length} remaining)
            </h4>
            {clipQuestions.map((question) => (
              <div
                key={question.id}
                className={`border rounded-lg p-3 ${
                  answers[question.id] ? 'bg-green-500/5 border-green-500/20' : ''
                }`}
              >
                <p className="text-xs font-medium mb-2">{question.question}</p>
                {question.context && (
                  <p className="text-[10px] text-muted-foreground mb-2 italic">
                    {question.context}
                  </p>
                )}
                <RadioGroup
                  value={answers[question.id] || ''}
                  onValueChange={(value) => onAnswerChange(question.id, value)}
                  className="space-y-1"
                >
                  {question.options?.map((option, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <RadioGroupItem value={option} id={`${question.id}-${i}`} />
                      <Label
                        htmlFor={`${question.id}-${i}`}
                        className="text-xs cursor-pointer"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                {answers[question.id] && (
                  <div className="flex items-center gap-1 mt-2 text-green-600">
                    <CheckCircle2 className="h-3 w-3" />
                    <span className="text-[10px]">Answered</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Other Questions */}
        {otherQuestions.length > 0 && (
          <div className="space-y-3 mb-4">
            <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <HelpCircle className="h-3 w-3" />
              Additional Questions
            </h4>
            {otherQuestions.map((question) => (
              <div
                key={question.id}
                className={`border rounded-lg p-3 ${
                  answers[question.id] ? 'bg-green-500/5 border-green-500/20' : ''
                }`}
              >
                <p className="text-xs font-medium mb-2">{question.question}</p>
                {question.context && (
                  <p className="text-[10px] text-muted-foreground mb-2 italic">
                    {question.context}
                  </p>
                )}
                {question.options && (
                  <RadioGroup
                    value={answers[question.id] || ''}
                    onValueChange={(value) => onAnswerChange(question.id, value)}
                    className="space-y-1"
                  >
                    {question.options.map((option, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`${question.id}-${i}`} />
                        <Label
                          htmlFor={`${question.id}-${i}`}
                          className="text-xs cursor-pointer"
                        >
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
                {answers[question.id] && (
                  <div className="flex items-center gap-1 mt-2 text-green-600">
                    <CheckCircle2 className="h-3 w-3" />
                    <span className="text-[10px]">Answered</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Edit Notes */}
        {analysis.editNotes && (
          <div className="bg-muted/50 rounded-lg p-3 text-xs">
            <p className="font-medium mb-1">Editor Notes</p>
            <p className="text-muted-foreground">{analysis.editNotes}</p>
          </div>
        )}
      </ScrollArea>

      {/* Refinement Input */}
      {onSendMessage && (
        <div className="mt-4 pt-4 border-t space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Refine: e.g., 'Use take 2 for scene 3', 'Add reaction shot after line'..."
              value={refinementInput}
              onChange={(e) => setRefinementInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendRefinement()}
              className="text-xs h-8"
              disabled={isRefining}
            />
            <Button
              size="sm"
              className="h-8 px-3"
              onClick={handleSendRefinement}
              disabled={!refinementInput.trim() || isRefining}
            >
              {isRefining ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Generate Plan Button */}
      <div className={`mt-4 ${onSendMessage ? '' : 'pt-4 border-t'}`}>
        <Button
          className="w-full bg-purple-600 hover:bg-purple-700"
          onClick={() => onGeneratePlan(selectedClipPerScene)}
          disabled={!canGenerate || isGenerating}
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating Edit Plan...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Generate Edit Plan
              {!canGenerate && ` (${unansweredQuestions.length} questions)`}
            </>
          )}
        </Button>
        {!canGenerate && (
          <p className="text-[10px] text-muted-foreground text-center mt-1">
            Answer all questions to generate the edit plan
          </p>
        )}
      </div>
    </Card>
  );
};
